/* ==========================================================================
   Kien Store — theme scripts (vanilla JS, no dependencies)
   Globals injected by layout/theme.liquid: window.Theme
   ========================================================================== */
(function () {
  'use strict';

  var Theme = window.Theme || {};
  Theme.routes = Theme.routes || {};
  Theme.strings = Theme.strings || {};
  Theme.settings = Theme.settings || {};

  /* ---------------------------------------------------------------- utils */

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(el, type, handler, options) {
    if (el) el.addEventListener(type, handler, options);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait || 200);
    };
  }

  /* Đọc text trước rồi mới parse: nếu Shopify trả body rỗng hoặc HTML thì
     res.json() ném ra lỗi kỹ thuật ("Unexpected end of JSON input") và lỗi đó
     lọt thẳng ra giao diện khách. Ở đây quy về một thông báo đọc được. */
  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      return res.text().then(function (text) {
        var data = null;

        if (text) {
          try {
            data = JSON.parse(text);
          } catch (e) {
            data = null;
          }
        }

        if (!data) {
          throw { description: Theme.strings.cartError, status: res.status };
        }

        if (!res.ok) throw data;
        return data;
      });
    });
  }

  var scrollLockCount = 0;

  function lockScroll() {
    scrollLockCount += 1;
    document.body.classList.add('is-locked');
  }

  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) document.body.classList.remove('is-locked');
  }

  /* ---------------------------------------------------------------- toast */

  var toastEl;
  var toastTimer;

  function toast(message) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    requestAnimationFrame(function () { toastEl.classList.add('is-visible'); });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 2600);
  }

  Theme.toast = toast;

  /* --------------------------------------------------------------- drawer */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  var openDrawers = [];

  function openDrawer(id, trigger) {
    var drawer = document.getElementById(id);
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer._trigger = trigger || document.activeElement;
    lockScroll();
    openDrawers.push(drawer);
    var focusTarget = drawer.querySelector('[data-drawer-focus]') || drawer.querySelector(FOCUSABLE);
    if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 120);
  }

  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    openDrawers = openDrawers.filter(function (d) { return d !== drawer; });
    if (drawer._trigger && typeof drawer._trigger.focus === 'function') drawer._trigger.focus();
  }

  function closeTopDrawer() {
    closeDrawer(openDrawers[openDrawers.length - 1]);
  }

  Theme.openDrawer = openDrawer;
  Theme.closeDrawer = closeDrawer;

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.getAttribute('data-drawer-open'), opener);
      return;
    }

    var closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('.drawer'));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeTopDrawer();
      return;
    }

    if (event.key !== 'Tab' || !openDrawers.length) return;

    var drawer = openDrawers[openDrawers.length - 1];
    var nodes = $$(FOCUSABLE, drawer).filter(function (el) { return el.offsetParent !== null; });
    if (!nodes.length) return;

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------ cart */

  var Cart = {
    countTargets: function () { return $$('[data-cart-count]'); },

    setCount: function (count) {
      Cart.countTargets().forEach(function (el) {
        el.textContent = count;
        el.hidden = Number(count) === 0;
      });
      var labels = $$('[data-cart-count-label]');
      var template = Theme.strings.cartCount || '~~count~~';
      labels.forEach(function (el) {
        el.textContent = template.replace('~~count~~', count);
      });
    },

    /* Một section nằm trong section group có id dạng
       "shopify-section-sections--123__cart-drawer" trên trang, nhưng Section
       Rendering API lại trả về wrapper id "shopify-section-cart-drawer".
       Phải khớp được cả hai dạng, nếu không giỏ hàng sẽ không bao giờ cập nhật. */
    findSection: function (key, root) {
      var scope = root || document;
      return (
        scope.querySelector('#shopify-section-' + key) ||
        scope.querySelector('[id^="shopify-section-"][id$="__' + key + '"]') ||
        scope.querySelector('[data-section-id="' + key + '"]')
      );
    },

    /* Thay markup giỏ hàng bằng HTML mới rồi đồng bộ số trên icon giỏ.
       Trả về số section thực sự được thay, để nơi gọi biết có cần tải lại không. */
    renderSections: function (sections) {
      if (!sections) return 0;

      var applied = 0;
      Object.keys(sections).forEach(function (key) {
        var html = sections[key];
        if (!html) return;

        var target = Cart.findSection(key, document);
        if (!target) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = Cart.findSection(key, parsed);
        if (!fresh) return;

        target.innerHTML = fresh.innerHTML;
        applied += 1;
      });

      var root = $('[data-cart-root]');
      if (root && root.hasAttribute('data-item-count')) {
        Cart.setCount(root.getAttribute('data-item-count'));
      }

      /* Markup vừa bị thay nên phải đồng bộ lại trạng thái nút tăng giảm */
      $$('.qty').forEach(syncQty);

      /* Cả khối giỏ hàng vừa bị thay bằng HTML mới, nên widget mà app bán thêm
         (Selleasy…) chèn vào đó đã biến mất cùng DOM cũ. Bắn tên sự kiện mà các
         app đó lắng nghe để chúng tự vẽ lại. Theme không nghe sự kiện này nên
         chưa cài app thì cũng không ai nhận. */
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { source: 'theme', itemCount: Number(root && root.getAttribute('data-item-count')) || 0 }
      }));

      return applied;
    },

    sectionsToRender: function () {
      return ['cart-drawer', 'main-cart'].filter(function (key) {
        return Cart.findSection(key, document);
      });
    },

    /* Phương án dự phòng: tự tải lại section giỏ hàng khi phản hồi thêm-vào-giỏ
       không kèm được HTML (ví dụ section bị đổi chỗ trong theme). */
    refresh: function () {
      var keys = Cart.sectionsToRender();
      if (!keys.length) return Promise.resolve(0);

      return fetch(window.location.pathname + '?sections=' + keys.join(','))
        .then(function (res) { return res.json(); })
        .then(function (data) { return Cart.renderSections(data); })
        .catch(function () { return 0; });
    },

    add: function (formData) {
      var sections = Cart.sectionsToRender();
      if (formData instanceof FormData) {
        if (sections.length) {
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        }
        return fetchJSON(Theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
      }

      var body = Object.assign({}, formData);
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      return fetchJSON(Theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    },

    change: function (payload) {
      var sections = Cart.sectionsToRender();
      var body = Object.assign({}, payload);
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      return fetchJSON(Theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    },

    updateNote: function (note) {
      return fetchJSON(Theme.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      });
    }
  };

  Theme.Cart = Cart;

  function setLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('btn--loading', !!isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    button.disabled = !!isLoading;
  }

  function handleCartError(error, scope) {
    var message = (error && (error.description || error.message)) || Theme.strings.cartError;
    var target = scope && scope.querySelector('[data-cart-error]');
    if (target) {
      target.textContent = message;
      target.hidden = false;
    } else {
      toast(message);
    }
  }

  /* Sản phẩm chính + các phụ kiện được tick, gộp thành một lần thêm giỏ.
     Không có phụ kiện nào thì giữ nguyên FormData để không mất line item
     properties mà Shopify hoặc app khác gắn vào form. */
  /* Ô nhập lời nhắn của một phụ kiện trên trang sản phẩm — chỉ món nào khai
     metafield `custom.addon_message_label` mới có. */
  function addonMessageField(root, input) {
    return root.querySelector('[data-addon-message="' + input.id + '"]');
  }

  /* Phụ kiện đã tick mà bỏ trống lời nhắn thì trả về đúng ô đó. Chặn ở đây rẻ
     hơn nhiều so với để đơn Note Card trống chữ rơi vào hộp thư hỗ trợ. */
  function missingAddonMessage(form) {
    var root = form.closest('[data-product-root]') || document;
    var checked = $$('[data-addon-input]:checked', root);
    for (var i = 0; i < checked.length; i++) {
      var field = addonMessageField(root, checked[i]);
      if (field && !field.value.trim()) return field;
    }
    return null;
  }
  function buildAddPayload(form) {
    var root = form.closest('[data-product-root]') || document;
    var checked = $$('[data-addon-input]:checked', root);
    if (!checked.length) return new FormData(form);

    var idInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');

    var items = [
      {
        id: Number(idInput && idInput.value),
        quantity: parseInt(qtyInput && qtyInput.value, 10) || 1,
      },
    ];

    checked.forEach(function (input) {
      var select = root.querySelector('[data-addon-variant-select="' + input.id + '"]');
      var variantId = select ? select.value : input.getAttribute('data-addon-variant');
      if (!variantId) return;

      var item = { id: Number(variantId), quantity: 1 };
      var message = addonMessageField(root, input);
      if (message && message.value.trim()) {
        item.properties = {};
        item.properties[message.getAttribute('data-message-label') || 'Message'] = message.value.trim();
      }
      items.push(item);
    });

    return { items: items };
  }

  /* Add-to-cart form submission (product page + quick add) */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('form[data-product-form]');
    if (!form) return;

    event.preventDefault();

    var button = form.querySelector('[data-add-to-cart]');
    var errorTarget = form.querySelector('[data-cart-error]');
    if (errorTarget) errorTarget.hidden = true;

    var blank = missingAddonMessage(form);
    if (blank) {
      handleCartError({ description: Theme.strings.addonMessageRequired }, form);
      blank.focus();
      return;
    }

    setLoading(button, true);

    Cart.add(buildAddPayload(form))
      .then(function (data) {
        var expected = Cart.sectionsToRender().length;
        var applied = Cart.renderSections(data.sections);

        /* Nếu Shopify không trả đủ HTML thì tự tải lại, tránh mở ra ngăn kéo cũ */
        var ready = applied >= expected ? Promise.resolve() : Cart.refresh();

        return ready.then(function () {
          toast(Theme.strings.addedToCart);

          if (Theme.settings.cartType === 'drawer' && document.getElementById('cart-drawer')) {
            openDrawer('cart-drawer', button);
          } else {
            window.location.href = Theme.routes.cart;
          }

          document.dispatchEvent(new CustomEvent('theme:cart:updated'));
        });
      })
      .catch(function (error) { handleCartError(error, form); })
      .finally(function () { setLoading(button, false); });
  });

  /* Nút "+" của khối gợi ý mua thêm trong ngăn kéo giỏ hàng. Ở đây không có
     form sản phẩm nào để gửi kèm, nên gọi thẳng /cart/add với đúng một biến thể.
     Phản hồi trả về HTML ngăn kéo mới, trong đó món vừa thêm đã bị loại khỏi
     danh sách gợi ý — không cần tự xoá dòng bằng tay. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-cart-addon-add]');
    if (!button) return;

    var row = button.closest('[data-cart-addon]');
    var scope = button.closest('.cart-addons');
    var errorTarget = scope && scope.querySelector('[data-cart-error]');
    if (errorTarget) errorTarget.hidden = true;

    /* Có ô chọn phân loại thì nghe ô đó, không thì lấy biến thể mặc định mà
       Liquid đã gắn sẵn lên nút. */
    var select = row && row.querySelector('[data-cart-addon-variant]');
    var variantId = Number(select ? select.value : button.getAttribute('data-addon-variant'));
    if (!variantId) return;

    var item = { id: variantId, quantity: 1 };
    var message = row && row.querySelector('[data-cart-addon-message]');
    if (message) {
      if (!message.value.trim()) {
        handleCartError({ description: Theme.strings.addonMessageRequired }, scope);
        message.focus();
        return;
      }
      item.properties = {};
      item.properties[message.getAttribute('data-message-label') || 'Message'] = message.value.trim();
    }

    setLoading(button, true);

    Cart.add({ items: [item] })
      .then(function (data) {
        var expected = Cart.sectionsToRender().length;
        var applied = Cart.renderSections(data.sections);
        var ready = applied >= expected ? Promise.resolve() : Cart.refresh();

        return ready.then(function () {
          toast(Theme.strings.addedToCart);
          document.dispatchEvent(new CustomEvent('theme:cart:updated'));
        });
      })
      .catch(function (error) { handleCartError(error, scope); })
      /* Ngăn kéo đã được vẽ lại nên `button` thường không còn trong trang —
         setLoading tự bỏ qua node rỗng, gọi vẫn an toàn cho nhánh lỗi. */
      .finally(function () { setLoading(button, false); });
  });

  /* Cart line changes (quantity + remove) */
  function changeLine(key, quantity, scope) {
    var root = $('[data-cart-root]');
    if (root) root.setAttribute('aria-busy', 'true');

    return Cart.change({ id: key, quantity: quantity })
      .then(function (data) {
        var expected = Cart.sectionsToRender().length;
        var applied = Cart.renderSections(data.sections);
        var ready = applied >= expected ? Promise.resolve() : Cart.refresh();

        return ready.then(function () {
          document.dispatchEvent(new CustomEvent('theme:cart:updated'));
        });
      })
      .catch(function (error) { handleCartError(error, scope); })
      .finally(function () {
        var current = $('[data-cart-root]');
        if (current) current.setAttribute('aria-busy', 'false');
      });
  }

  document.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.getAttribute('data-cart-remove'), 0, remove.closest('.cart-item'));
      return;
    }

    var qtyBtn = event.target.closest('[data-qty-change]');
    if (!qtyBtn) return;

    var wrap = qtyBtn.closest('.qty');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;

    var step = qtyBtn.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    var bounds = qtyBounds(input);
    var next = Math.min(bounds.max, Math.max(bounds.min, (parseInt(input.value, 10) || bounds.min) + step));

    if (next === parseInt(input.value, 10)) return;
    input.value = next;
    syncQty(wrap);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Mờ nút khi đã chạm biên, để khách không bấm vào chỗ không có tác dụng */
  function qtyBounds(input) {
    return {
      min: parseInt(input.getAttribute('min') || '1', 10),
      max: input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity,
    };
  }

  function syncQty(wrap) {
    if (!wrap) return;

    var input = wrap.querySelector('input');
    if (!input) return;

    var bounds = qtyBounds(input);
    var value = parseInt(input.value, 10) || bounds.min;

    var down = wrap.querySelector('[data-qty-change="down"]');
    var up = wrap.querySelector('[data-qty-change="up"]');

    if (down) down.disabled = value <= bounds.min;
    if (up) up.disabled = value >= bounds.max;
  }

  document.addEventListener('input', function (event) {
    if (event.target.matches('.qty__input')) syncQty(event.target.closest('.qty'));
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    var quantity = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(input.getAttribute('data-cart-quantity'), quantity, input.closest('.cart-item'));
  });

  var saveNote = debounce(function (value) { Cart.updateNote(value); }, 500);

  document.addEventListener('input', function (event) {
    if (event.target.matches('[data-cart-note]')) saveNote(event.target.value);
  });

  /* ------------------------------------------------------- variant picker */

  function VariantPicker(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.productUrl = root.getAttribute('data-product-url');
    this.variants = [];

    var dataEl = root.querySelector('[data-variant-json]');
    if (dataEl) {
      try { this.variants = JSON.parse(dataEl.textContent); } catch (e) { this.variants = []; }
    }

    this.onChange = this.onChange.bind(this);
    root.addEventListener('change', this.onChange);
  }

  VariantPicker.prototype.selectedOptions = function () {
    return $$('fieldset[data-option-index]', this.root).map(function (fieldset) {
      var checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  };

  VariantPicker.prototype.findVariant = function () {
    var selected = this.selectedOptions();
    return this.variants.find(function (variant) {
      return selected.every(function (value, index) {
        return value === null || variant.options[index] === value;
      });
    });
  };

  VariantPicker.prototype.onChange = function (event) {
    if (!event.target.matches('input[data-option-value]')) return;

    var fieldset = event.target.closest('fieldset[data-option-index]');
    var label = fieldset && fieldset.querySelector('[data-option-selected]');
    if (label) label.textContent = event.target.value;

    var variant = this.findVariant();
    this.updateUrl(variant);
    this.updateIdInput(variant);
    this.updateMedia(variant);
    this.renderSection(variant);
  };

  VariantPicker.prototype.updateUrl = function (variant) {
    if (!variant || !this.productUrl) return;
    window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
  };

  VariantPicker.prototype.updateIdInput = function (variant) {
    var input = $('[data-variant-id]', this.root.closest('[data-product-root]') || document);
    if (input) input.value = variant ? variant.id : '';
  };

  VariantPicker.prototype.updateMedia = function (variant) {
    if (!variant || !variant.featured_media) return;
    var gallery = $('[data-product-gallery][data-section-id="' + this.sectionId + '"]');
    if (!gallery) return;
    var thumb = gallery.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
    if (thumb) thumb.click();
  };

  VariantPicker.prototype.renderSection = function (variant) {
    var scope = this.root.closest('[data-product-root]') || document;
    var url = this.productUrl + '?variant=' + (variant ? variant.id : '') + '&section_id=' + this.sectionId;

    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        ['[data-price-target]', '[data-buy-target]', '[data-inventory-target]', '[data-sku-target]'].forEach(function (selector) {
          var fresh = parsed.querySelector(selector);
          var current = scope.querySelector(selector);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
      })
      .catch(function () { /* keep current markup on failure */ });
  };

  /* --------------------------------------------------------- product media */

  function initGallery(gallery) {
    on(gallery, 'click', function (event) {
      var thumb = event.target.closest('[data-media-id]');
      if (!thumb) return;
      var id = thumb.getAttribute('data-media-id');

      $$('[data-media-id]', gallery).forEach(function (el) {
        el.classList.toggle('is-active', el === thumb);
        if (el.hasAttribute('aria-pressed')) el.setAttribute('aria-pressed', el === thumb ? 'true' : 'false');
      });

      $$('[data-media-slide]', gallery).forEach(function (slide) {
        slide.classList.toggle('is-active', slide.getAttribute('data-media-slide') === id);
      });
    });
  }

  /* ------------------------------------------------------------- accordion */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('.accordion__trigger');
    if (!trigger) return;

    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));

    trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (panel) panel.setAttribute('data-open', expanded ? 'false' : 'true');
  });

  /* ------------------------------------------------------------ mobile nav */

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-mobile-submenu]');
    if (!toggle) return;
    event.preventDefault();
    var item = toggle.closest('.mobile-nav__item');
    if (!item) return;
    var open = item.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* ------------------------------------------------------ predictive search */

  function initPredictiveSearch(form) {
    var input = form.querySelector('input[name="q"]');
    var results = document.getElementById(form.getAttribute('data-results-target'));
    if (!input || !results) return;

    var run = debounce(function () {
      var term = input.value.trim();
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }

      var url = Theme.routes.predictiveSearch +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,collection,page,article' +
        '&resources[limit]=5' +
        '&section_id=predictive-search';

      fetch(url)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-predictive-results]');
          results.innerHTML = fresh ? fresh.innerHTML : '';
        })
        .catch(function () { results.innerHTML = ''; });
    }, 250);

    on(input, 'input', run);
  }

  /* ---------------------------------------------------------------- facets */

  function applyFacets(url) {
    var form = $('[data-facets-form]');
    if (!form) return;

    var sectionId = form.getAttribute('data-section-id');
    var results = $('[data-facets-results]');
    if (results) results.setAttribute('aria-busy', 'true');

    fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + sectionId)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        ['[data-facets-results]', '[data-facets-form]', '[data-active-facets]', '[data-facets-count]'].forEach(function (selector) {
          var fresh = parsed.querySelector(selector);
          var current = $(selector);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        window.history.pushState({}, '', url);
        var updated = $('[data-facets-results]');
        if (updated) updated.setAttribute('aria-busy', 'false');
      })
      .catch(function () { window.location.href = url; });
  }

  /* Delegated once — the facets form is re-rendered on every apply. */
  document.addEventListener('change', function (event) {
    var form = event.target.closest('[data-facets-form]');
    if (!form) return;

    var params = new URLSearchParams(new FormData(form));
    var clean = new URLSearchParams();
    params.forEach(function (value, key) { if (value !== '') clean.append(key, value); });
    applyFacets(window.location.pathname + '?' + clean.toString());
  });

  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-facet-remove]');
    if (!link) return;
    event.preventDefault();
    applyFacets(link.getAttribute('href'));
  });

  /* ------------------------------------------------------------- slideshow */

  function initSlideshow(root) {
    var slides = $$('[data-slide]', root);
    if (slides.length < 2) return;

    var dots = $$('[data-slide-dot]', root);
    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 6000;
    var index = 0;
    var timer;

    function go(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    function start() {
      if (!autoplay) return;
      stop();
      timer = setInterval(function () { go(index + 1); }, interval);
    }

    function stop() { clearInterval(timer); }

    dots.forEach(function (dot, i) {
      on(dot, 'click', function () { go(i); start(); });
    });

    on(root, 'mouseenter', stop);
    on(root, 'mouseleave', start);
    go(0);
    start();
  }

  /* -------------------------------------------------------------- carousel */

  function initCarousel(root) {
    var track = root.querySelector('[data-carousel-track]');
    if (!track) return;

    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');

    /* Cuộn gần trọn khung nhìn, chừa lại một chút để khách thấy mạch nối */
    function pageSize() {
      return Math.max(track.clientWidth * 0.85, 200);
    }

    function sync() {
      var overflowing = track.scrollWidth > track.clientWidth + 2;
      root.classList.toggle('is-overflowing', overflowing);
      track.classList.toggle('is-overflowing', overflowing);

      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }

    on(prev, 'click', function () { track.scrollBy({ left: -pageSize(), behavior: 'smooth' }); });
    on(next, 'click', function () { track.scrollBy({ left: pageSize(), behavior: 'smooth' }); });
    on(track, 'scroll', debounce(sync, 60));
    window.addEventListener('resize', debounce(sync, 150));

    sync();
    /* Ảnh tải xong mới biết chiều rộng thật, đo lại một lần nữa */
    setTimeout(sync, 400);
  }

  /* ---------------------------------------------------------- sticky header */

  function syncHeaderHeight() {
    var header = $('[data-header]');
    if (!header) return;
    document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
  }

  /* --------------------------------------------------------- reveal on view */

  function initReveal() {
    var items = $$('.reveal:not(.is-visible)');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------ init */

  /* Chạy từng phần khởi tạo trong vỏ bọc riêng. Trước đây một lỗi ở giữa init()
     là các bước sau không chạy — trong đó có initReveal(), nên toàn bộ nội dung
     mang class .reveal nằm im ở opacity 0 và trang trông như trắng trơn. */
  function safe(name, fn) {
    try {
      fn();
    } catch (error) {
      if (window.console && console.error) console.error('[theme] ' + name, error);
    }
  }

  function init(scope) {
    scope = scope || document;

    /* Hiện nội dung TRƯỚC mọi thứ khác: không bao giờ để hiệu ứng xuất hiện
       chặn mất nội dung nếu phía dưới có gì đó hỏng. */
    safe('reveal', initReveal);

    $$('[data-variant-picker]', scope).forEach(function (el) {
      if (!el._picker) el._picker = new VariantPicker(el);
    });

    $$('[data-product-gallery]', scope).forEach(function (el) {
      if (el._init) return;
      el._init = true;
      initGallery(el);
    });

    $$('[data-predictive-search]', scope).forEach(function (el) {
      if (el._init) return;
      el._init = true;
      initPredictiveSearch(el);
    });

    $$('[data-slideshow]', scope).forEach(function (el) {
      if (el._init) return;
      el._init = true;
      initSlideshow(el);
    });

    safe('carousel', function () {
      $$('[data-carousel]', scope).forEach(function (el) {
        if (el._init) return;
        el._init = true;
        initCarousel(el);
      });
    });

    safe('quantity', function () {
      $$('.qty', scope).forEach(syncQty);
    });

    safe('reviews', function () {
      $$('[data-reviews]', scope).forEach(function (el) {
        if (el._init) return;
        el._init = true;
        initReviews(el);
      });
    });

    safe('header height', syncHeaderHeight);
  }

  /* --------------------------------------------------------------- reviews */

  /* Ô đánh giá cao bằng nhau nên phần chữ bị -webkit-line-clamp cắt. Nút "Xem
     thêm" chỉ hiện khi chữ THẬT SỰ bị cắt, so scrollHeight với clientHeight —
     đo sau khi font đã nạp, không thì mọi ô đều tưởng là bị cắt. */
  function initReviewClamps(root) {
    $$('[data-review-body]', root).forEach(function (body) {
      var card = body.closest('[data-review]');
      var more = card && card.querySelector('[data-review-more]');
      if (!more) return;

      if (body.scrollHeight - body.clientHeight > 2) {
        more.hidden = false;
      } else {
        more.hidden = true;
      }
    });
  }

  function initReviews(panel) {
    /* Mở / đóng form viết đánh giá */
    var form = panel.querySelector('[data-reviews-form]');
    var toggles = $$('[data-reviews-toggle]', panel);

    toggles.forEach(function (btn) {
      on(btn, 'click', function () {
        if (!form) return;
        var opening = form.hidden;
        form.hidden = !opening;

        toggles.forEach(function (t) {
          t.setAttribute('aria-expanded', opening ? 'true' : 'false');
          var openLabel = t.querySelector('[data-reviews-toggle-open]');
          var closeLabel = t.querySelector('[data-reviews-toggle-close]');
          if (openLabel) openLabel.hidden = opening;
          if (closeLabel) closeLabel.hidden = !opening;
        });

        if (opening) {
          var first = form.querySelector('input[type="text"], textarea');
          if (first) first.focus();
        }
      });
    });

    /* Gửi đánh giá không tải lại trang.

       Shopify không có endpoint JSON cho `{% form 'contact' %}` — nó nhận POST
       rồi chuyển hướng về chính trang này, và theme in ra .form__message--success
       hoặc .form__message--error. Nên cách đọc kết quả là POST bằng fetch rồi
       dò hai class đó trong HTML trả về.

       Không dò ra thì KHÔNG đoán bừa là thành công: bật cờ để lần bấm sau gửi
       theo kiểu cũ (tải lại trang). Thà khách thấy trang nháy một cái còn hơn
       mình báo "đã gửi" trong khi đánh giá rơi mất. */
    var reviewForm = form && form.querySelector('form');

    if (reviewForm) {
      var fallback = false;

      on(reviewForm, 'submit', function (event) {
        if (fallback) return; /* để trình duyệt gửi kiểu thường */
        if (!window.fetch || !window.FormData || !window.DOMParser) return;

        event.preventDefault();

        var submitBtn = reviewForm.querySelector('button[type="submit"]');
        var box = reviewForm.querySelector('[data-reviews-message]');
        if (submitBtn) submitBtn.disabled = true;

        var show = function (text, ok) {
          if (!box) return;
          box.textContent = text;
          box.classList.toggle('form__message--success', ok);
          box.classList.toggle('form__message--error', !ok);
          box.hidden = false;
        };

        fetch(reviewForm.action, {
          method: 'POST',
          body: new FormData(reviewForm),
          headers: { Accept: 'text/html' },
        })
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var err = doc.querySelector('.form__message--error');

            if (err) {
              show(err.textContent.trim(), false);
              if (submitBtn) submitBtn.disabled = false;
              return;
            }

            if (!doc.querySelector('.form__message--success')) {
              throw new Error('không đọc được kết quả');
            }

            /* Thành công: giấu mọi thứ trong form, chỉ chừa lời cảm ơn */
            $$(':scope > *', reviewForm).forEach(function (el) {
              if (!el.hasAttribute('data-reviews-message')) el.hidden = true;
            });
            show(form.getAttribute('data-reviews-thanks') || '', true);
          })
          .catch(function () {
            fallback = true;
            show(form.getAttribute('data-reviews-failed') || '', false);
            if (submitBtn) submitBtn.disabled = false;
          });
      });
    }

    /* Xem thêm / thu lại */
    on(panel, 'click', function (event) {
      var btn = event.target.closest('[data-review-more]');
      if (!btn) return;

      var card = btn.closest('[data-review]');
      var body = card && card.querySelector('[data-review-body]');
      if (!body) return;

      var expanded = body.classList.toggle('is-expanded');
      var openLabel = btn.querySelector('[data-review-more-open]');
      var closeLabel = btn.querySelector('[data-review-more-close]');
      if (openLabel) openLabel.hidden = expanded;
      if (closeLabel) closeLabel.hidden = !expanded;
    });

    /* Sắp xếp: đổi thứ tự node ngay trên trang, không gọi lại server */
    var grid = panel.querySelector('[data-reviews-grid]');
    var sort = panel.querySelector('[data-reviews-sort]');

    if (grid && sort) {
      var original = $$('[data-review]', grid);

      var num = function (el, name) {
        return parseInt(el.getAttribute(name), 10) || 0;
      };

      var comparators = {
        pictures: function (a, b) {
          var diff = (num(b, 'data-review-images') > 0) - (num(a, 'data-review-images') > 0);
          return diff || num(b, 'data-review-date') - num(a, 'data-review-date');
        },
        newest: function (a, b) {
          return num(b, 'data-review-date') - num(a, 'data-review-date');
        },
        highest: function (a, b) {
          return num(b, 'data-review-rating') - num(a, 'data-review-rating') ||
            num(b, 'data-review-date') - num(a, 'data-review-date');
        },
        lowest: function (a, b) {
          return num(a, 'data-review-rating') - num(b, 'data-review-rating') ||
            num(b, 'data-review-date') - num(a, 'data-review-date');
        },
      };

      on(sort, 'change', function () {
        var compare = comparators[sort.value];
        if (!compare) return;
        original
          .slice()
          .sort(compare)
          .forEach(function (card) { grid.appendChild(card); });
        /* Thứ tự ảnh trong lightbox phải khớp thứ tự đang thấy trên trang */
        ReviewLightbox.refresh();
      });

      sort.dispatchEvent(new Event('change'));
    }

    initReviewClamps(panel);
  }

  /* Lightbox gộp toàn bộ ảnh của mọi đánh giá trên trang, nên đếm được kiểu
     "9 / 21" và vuốt xuyên từ đánh giá này sang đánh giá khác. */
  var ReviewLightbox = {
    sources: [],
    index: 0,

    el: function () { return $('[data-review-lightbox]'); },

    /* Đọc lại danh sách ảnh theo đúng thứ tự đang hiển thị. Ảnh chính và dãy
       thumbnail của cùng một đánh giá trỏ tới cùng file, nên lọc trùng liên
       tiếp để không đếm đôi. */
    refresh: function () {
      var seen = {};
      ReviewLightbox.sources = $$('[data-review-image]')
        .map(function (btn) { return btn.getAttribute('data-review-src'); })
        .filter(function (src) {
          if (!src || seen[src]) return false;
          seen[src] = true;
          return true;
        });
    },

    open: function (src) {
      var box = ReviewLightbox.el();
      if (!box) return;

      if (!ReviewLightbox.sources.length) ReviewLightbox.refresh();

      var at = ReviewLightbox.sources.indexOf(src);
      ReviewLightbox.index = at > -1 ? at : 0;

      ReviewLightbox.buildThumbs();
      ReviewLightbox.show();

      box.classList.add('is-open');
      box.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
    },

    close: function () {
      var box = ReviewLightbox.el();
      if (!box) return;
      box.classList.remove('is-open');
      box.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
    },

    step: function (delta) {
      var total = ReviewLightbox.sources.length;
      if (!total) return;
      ReviewLightbox.index = (ReviewLightbox.index + delta + total) % total;
      ReviewLightbox.show();
    },

    show: function () {
      var box = ReviewLightbox.el();
      if (!box) return;

      var img = box.querySelector('[data-review-lightbox-image]');
      var counter = box.querySelector('[data-review-lightbox-counter]');
      var total = ReviewLightbox.sources.length;

      if (img) img.src = ReviewLightbox.sources[ReviewLightbox.index] || '';
      if (counter) counter.textContent = (ReviewLightbox.index + 1) + ' / ' + total;

      $$('.rv-lb__thumb', box).forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === ReviewLightbox.index);
      });

      var active = box.querySelector('.rv-lb__thumb.is-active');
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'center' });
      }
    },

    buildThumbs: function () {
      var box = ReviewLightbox.el();
      if (!box) return;
      var wrap = box.querySelector('[data-review-lightbox-thumbs]');
      if (!wrap || wrap.childElementCount === ReviewLightbox.sources.length) return;

      wrap.innerHTML = '';
      ReviewLightbox.sources.forEach(function (src, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rv-lb__thumb';
        btn.setAttribute('aria-label', i + 1 + ' / ' + ReviewLightbox.sources.length);
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        btn.appendChild(img);
        on(btn, 'click', function () {
          ReviewLightbox.index = i;
          ReviewLightbox.show();
        });
        wrap.appendChild(btn);
      });
    },
  };

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-review-image]');
    if (opener) {
      event.preventDefault();
      ReviewLightbox.open(opener.getAttribute('data-review-src'));
      return;
    }

    if (event.target.closest('[data-review-lightbox-close]')) {
      ReviewLightbox.close();
      return;
    }

    if (event.target.closest('[data-review-lightbox-prev]')) {
      ReviewLightbox.step(-1);
      return;
    }

    if (event.target.closest('[data-review-lightbox-next]')) {
      ReviewLightbox.step(1);
    }
  });

  document.addEventListener('keydown', function (event) {
    var box = ReviewLightbox.el();
    if (!box || !box.classList.contains('is-open')) return;

    if (event.key === 'Escape') ReviewLightbox.close();
    if (event.key === 'ArrowLeft') ReviewLightbox.step(-1);
    if (event.key === 'ArrowRight') ReviewLightbox.step(1);
  });

  /* Lưới an toàn cuối cùng: dù có chuyện gì xảy ra, sau 2 giây mọi khối vẫn
     phải hiện ra. Thà mất hiệu ứng còn hơn mất nội dung. */
  function forceReveal() {
    $$('.reveal:not(.is-visible)').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  setTimeout(forceReveal, 2000);
  window.addEventListener('load', function () { setTimeout(forceReveal, 300); });
  window.addEventListener('load', function () { initReviewClamps(document); });
  window.addEventListener('resize', debounce(function () { initReviewClamps(document); }, 200));

  document.addEventListener('DOMContentLoaded', function () { init(document); });
  window.addEventListener('resize', debounce(syncHeaderHeight, 150));

  /* Theme editor hooks — re-init whichever section was re-rendered */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:select', function (event) {
    if (event.target.querySelector('[data-drawer-open="cart-drawer"]')) return;
    init(event.target);
  });

  window.Theme = Theme;
})();
